import React from 'react';
import styles from './StoreDetailDrawer.module.css';
import { TierBadge } from './TierBadge';
import { StoreItem } from '../storeTieringTypes';

interface StoreDetailDrawerProps {
  store: StoreItem | null;
  open: boolean;
  onClose: () => void;
}

export const StoreDetailDrawer: React.FC<StoreDetailDrawerProps> = ({ store, open, onClose }) => {
  if (!store) return null;

  const isUp = store.trendDirection === 'up';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${open ? styles.backdropVisible : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`${styles.drawer} ${open ? styles.drawerOpen : ''}`}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>Store Tiering Details</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Store Identity */}
        <div className={styles.identity}>
          <TierBadge tier={store.tier} />
          <div>
            <p className={styles.identityName}>{store.storeName}</p>
            <p className={styles.identityMeta}>{store.city} {store.zipCode}</p>
            <p className={styles.identityMeta}>{store.route} · {store.activeDays}</p>
          </div>
        </div>

        {/* Revenue Card */}
        <div className={styles.revenueCard}>
          <div>
            <p className={styles.revenueValue}>${store.weeklyRevenue.toLocaleString()}</p>
            <p className={styles.revenueLabel}>/week</p>
          </div>
          <span className={isUp ? styles.revTrendUp : styles.revTrendDown}>
            <svg className={styles.revTrendArrow} viewBox="0 0 16 16" fill="currentColor">
              {isUp ? (
                <path d="M8 4l4 4H9v4H7V8H4l4-4z"/>
              ) : (
                <path d="M8 12l-4-4h3V4h2v4h3l-4 4z"/>
              )}
            </svg>
            {store.trendPercent > 0 ? '+' : ''}{store.trendPercent}%
          </span>
        </div>

        {/* Metrics Grid */}
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <span className={styles.metricLabel}>Weekly Target</span>
            <span className={styles.metricValueGreen}>
              {store.target ? `$${store.target.toLocaleString()}` : '—'}
            </span>
          </div>
          <div className={styles.metricCard}>
            <span className={styles.metricLabel}>Margin</span>
            <span className={styles.metricValueGreen}>{store.margin.toFixed(1)}%</span>
          </div>
          <div className={styles.metricCard}>
            <span className={styles.metricLabel}>Orders/Week</span>
            <span className={styles.metricValue}>{store.ordersPerWeek.toFixed(1)}x</span>
          </div>
          <div className={styles.metricCard}>
            <span className={styles.metricLabel}>AOV</span>
            <span className={styles.metricValue}>${store.aov.toLocaleString()}</span>
          </div>
        </div>

        {/* Last Order */}
        <div className={styles.lastOrderCard}>
          <span className={styles.metricLabel}>Last Order</span>
          <span className={styles.lastOrderValue}>{store.lastOrder} ago</span>
        </div>

        {/* Recommended Actions */}
        <div className={styles.actionsCard}>
          <p className={styles.actionsTitle}>
            <svg className={styles.actionsIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 2v11h3v9l7-12h-4l4-8z"/>
            </svg>
            Recommended Actions:
          </p>
          {store.recommendedActions.map((action, i) => (
            <p key={i} className={styles.actionItem}>· {action}</p>
          ))}
        </div>

        {/* Tier Duration */}
        <div className={styles.tierDuration}>
          <svg className={styles.calIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
          </svg>
          In tier {store.tier} for {store.tierMonths} months
        </div>

        <div className={styles.divider} />

        {/* Store Information */}
        <h3 className={styles.infoTitle}>Store Information</h3>
        <div className={styles.infoList}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Owner</span>
            <span className={styles.infoValue}>{store.owner}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Phone</span>
            <span className={styles.infoValue}>{store.phone}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>{store.email}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Sales Rep</span>
            <span className={styles.infoValue}>{store.salesRep}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Address</span>
            <span className={styles.infoValue}>{store.fullAddress}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>Frequency</span>
            <span className={styles.infoValue}>{store.frequency} per week</span>
          </div>
        </div>
      </div>
    </>
  );
};
